import { useEffect, useState } from 'react';
import { X, Info } from 'lucide-react';
import { apiClient } from '../../../api';
import type { AppDetail } from './types';

function Field({ label, value, source }: { label: string; value: unknown; source?: string }) {
  const text = value == null || value === '' ? '—' : String(value);
  return (
    <div className="min-w-[140px]">
      <div className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider flex items-center gap-1.5">
        {label}
        {source && <span className="normal-case tracking-normal font-medium text-gray-300">{source}</span>}
      </div>
      <div className="text-xs text-gray-800 mt-0.5 break-words">{text}</div>
    </div>
  );
}

export default function AppDetailPanel({ eai, onClose }: { eai: number; onClose: () => void }) {
  const [app, setApp] = useState<AppDetail | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setApp(null);
    apiClient.get(`/api/cloud-portfolio/apps/${eai}`)
      .then((r) => { if (!cancelled) { setApp(r.data); setError(null); } })
      .catch((e) => { if (!cancelled) setError(e?.response?.data?.error || 'Not found'); });
    return () => { cancelled = true; };
  }, [eai]);

  const p2 = app?.phase2_full || {};

  return (
    <aside className="w-[380px] flex-shrink-0 border border-gray-200 rounded-xl overflow-hidden bg-white flex flex-col max-h-[640px]">
      <div className="px-4 py-3 border-b border-gray-100 bg-gray-50 flex items-center justify-between">
        <div className="min-w-0">
          <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">EAI {eai}</p>
          <p className="text-sm font-semibold text-super-dark-purple truncate">{app?.name || '…'}</p>
        </div>
        <button onClick={onClose} className="p-1 rounded hover:bg-gray-200 text-gray-500">
          <X className="w-4 h-4" />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto p-4 space-y-4 text-xs">
        {error && <p className="text-amber-700">{error}</p>}
        {!app && !error && <p className="text-gray-400">Loading…</p>}
        {app && (
          <>
            <section className="rounded-lg border border-gray-200 bg-gray-50 p-3 space-y-2">
              <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">SoR · GRC / ServiceNow</p>
              <div className="grid grid-cols-2 gap-2">
                <Field label="Disposition" value={app.sor_disposition} source="SoR" />
                <Field label="Tier" value={app.sor_tier} source="SoR" />
                <Field label="RPO" value={app.sor_rpo} source="SoR" />
                <Field label="RTO" value={app.sor_rto} source="SoR" />
              </div>
            </section>

            <section className="rounded-lg border border-emerald-200 bg-emerald-50/40 p-3 space-y-2">
              <p className="text-[11px] font-semibold text-emerald-700/70 uppercase tracking-wider">Phase 2 · analysis</p>
              <div className="grid grid-cols-2 gap-2">
                <Field label="PHC Disposition" value={app.phc_disposition} source="P2" />
                <Field label="New Tier" value={app.new_tier} source="P2" />
                <Field label="New RPO" value={app.new_rpo} source="P2" />
                <Field label="New RTO" value={app.new_rto} source="P2" />
                <Field label="Pattern" value={app.pattern} source="P2" />
                <Field label="Primary cloud" value={app.primary_cloud_disposition} source="P2" />
                <Field label="Progress" value={app.cloud_migration_progress} source="P2" />
                <Field label="Category" value={app.cloud_category == null ? 'N/A' : app.cloud_category} source="P2" />
                <Field label="Willingness" value={app.cloud_willingness} />
                <Field label="Size of effort" value={app.size_of_effort} />
                <Field label="Cloud target" value={app.cloud_target} />
                <Field label="Grouping" value={app.proposed_grouping} />
              </div>
              {p2.phc_infra_description ? (
                <Field label="PHC infra description" value={p2.phc_infra_description} />
              ) : null}
            </section>

            <section className="rounded-lg border border-amber-200 bg-amber-50/50 p-3 space-y-2">
              <p className="text-[11px] font-semibold text-amber-700/80 uppercase tracking-wider">Phase 3 · tracker</p>
              {app.tracker_items.length === 0 ? (
                <p className="text-gray-400">Not on the Phase 3 tracker.</p>
              ) : app.tracker_items.map((t) => (
                <div key={t.id} className="grid grid-cols-2 gap-2">
                  <Field label="Current status" value={t.current_status} source="P3" />
                  <Field label="Owner" value={t.owner} />
                  <Field label="Assessed?" value={t.assessed} />
                  <Field label="POA / landing" value={t.poa_decision || t.landing_aligned} />
                  <Field label="GFD" value={t.gfd_required} />
                  <Field label="Complete?" value={t.complete} />
                  <Field label="Blockers" value={t.blockers} />
                  <Field label="Notes" value={t.notes} />
                </div>
              ))}
            </section>

            <section className="rounded-lg border border-dashed border-gray-300 bg-gray-50 p-3">
              <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-1">Impact · Victor</p>
              <div className="flex items-start gap-2 text-gray-500">
                <Info className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                {app.impact.available
                  ? `${app.impact.rows} footprint row(s).`
                  : 'Victor extract is not imported — no VM / Oracle / cost figures.'}
              </div>
            </section>

            <section>
              <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-2">SN remediations</p>
              {app.remediations.length === 0 ? (
                <p className="text-gray-400">None for this EAI.</p>
              ) : (
                <ul className="space-y-2">
                  {app.remediations.map((r) => (
                    <li key={r.id} className="border border-gray-200 rounded-lg px-3 py-2">
                      <p className="font-semibold text-gray-800">{r.correction || '—'}</p>
                      <p className="text-gray-500 mt-0.5">{r.status || '—'} · {r.poc || 'no POC'}</p>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <section>
              <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-2">Attachments</p>
              {app.baseball_cards.length === 0 ? (
                <p className="text-gray-400">No baseball-card suite linked. Attach a PPTX on Imports.</p>
              ) : app.baseball_cards.map((c) => (
                <p key={c.id} className="text-gray-700">{c.suite_title || c.filename}</p>
              ))}
            </section>

            {app.out_of_scope && (
              <div className="bg-amber-50 text-amber-700 border border-amber-200 rounded-lg px-3 py-2">
                Out of scope: {app.oos_reason || 'reason not recorded'}
                {app.scope_conflict ? ' · also present on Application Details' : ''}
              </div>
            )}
          </>
        )}
      </div>
    </aside>
  );
}
